/**
 * Project Bobcat — live-agent handoff when the project may have no live agent
 * platform (Edan's review, Oct 27, option A).
 *
 * The template's escalation node (999, "Agent escalation") transfers with
 * `xfer_to_agent`. With no platform connected that transfer goes nowhere, so
 * the node instead tells the customer no one is available and the solution
 * keeps helping. Design says which state a solution is in and offers the way
 * on: set up a platform, then turn the transfer back on. Pure.
 */

export const TRANSFER_BEHAVIOR = 'xfer_to_agent';

/** What the escalation node says while it transfers (the template's text). */
export const TRANSFER_MESSAGE = 'Transferring you to a live representative.';

/** What it says when there is no one to transfer to. */
export const NO_AGENT_MESSAGE =
  'I’m sorry, no one is available to chat right now. I can keep helping here. What else can I do for you?';

export interface HandoffRow {
  id: string;
  nodeNumber?: unknown;
  nodeName?: unknown;
  nodeType?: unknown;
  message?: unknown;
  behaviors?: unknown;
}

const behaviorList = (v: unknown): string[] =>
  (Array.isArray(v) ? v : typeof v === 'string' ? v.split(/[,|]/) : [])
    .map((b) => String(b ?? '').trim())
    .filter(Boolean);

export const transfers = (row: HandoffRow): boolean =>
  behaviorList(row.behaviors).includes(TRANSFER_BEHAVIOR);

/**
 * The solution's escalation node: the one that transfers, or else the
 * template's 999 / "Agent escalation" with its transfer turned off.
 */
export const findEscalationRow = <R extends HandoffRow>(
  rows: R[]
): R | undefined =>
  rows.find(transfers) ??
  rows.find(
    (r) =>
      String(r.nodeNumber ?? '') === '999' ||
      /^agent escalation$/i.test(String(r.nodeName ?? '').trim())
  );

export type HandoffState =
  /** Not known yet: the platform list hasn't loaded. */
  | 'unknown'
  /** The solution has no escalation node at all. */
  | 'none'
  /** No live agent platform is connected. */
  | 'no-platform'
  /** A platform is connected, but the escalation node doesn't transfer. */
  | 'off'
  /** Customers who ask for a person are transferred. */
  | 'on';

export const handoffState = (
  hasPlatform: boolean | null,
  rows: HandoffRow[]
): HandoffState => {
  const row = findEscalationRow(rows);
  if (!row) return 'none';
  if (hasPlatform === null) return 'unknown';
  if (!hasPlatform) return 'no-platform';
  return transfers(row) ? 'on' : 'off';
};

/** The escalation node's message and behaviors with the transfer off. */
export const handoffOffFields = (row: HandoffRow) => ({
  message: NO_AGENT_MESSAGE,
  behaviors: behaviorList(row.behaviors).filter((b) => b !== TRANSFER_BEHAVIOR),
});

/** …and with it back on. Keeps a message someone wrote themselves. */
export const handoffOnFields = (row: HandoffRow) => {
  const message = typeof row.message === 'string' ? row.message.trim() : '';
  return {
    message:
      !message || message === NO_AGENT_MESSAGE ? TRANSFER_MESSAGE : message,
    behaviors: [
      ...behaviorList(row.behaviors).filter((b) => b !== TRANSFER_BEHAVIOR),
      TRANSFER_BEHAVIOR,
    ],
  };
};
